import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {Box, Button,Link, Checkbox, FormControlLabel, Stack, TextField, Typography,} from "@mui/material";
import { useNavigate } from "react-router";
import { useMutation } from '@tanstack/react-query';
import { DefaultError } from '@tanstack/query-core';
import { AxiosError } from 'axios';
import { register, RegisterPayload } from "../../service/auth.service";
import { useAuthStore } from "../../store/auth.store";
import { enqueueSnackbar } from "notistack";
import { TextInput } from "../form/TextInput";


const LoginSchema = Yup.object().shape({
    login: Yup.string().email('Should be valid Email').required("Login is required"),
    password: Yup.string().required("Password is required"),
});


export function RegisterForm ({setLoading}:{setLoading: (b: boolean) => void}){
    const initialValues: RegisterPayload = { login: "", password: "" };
    const [repeatPassword, setRepeatPassword] = useState('');
    const navigate = useNavigate();
    const authStore = useAuthStore();
    const [agreed, setAgreed] = React.useState(false);

    const mutation = useMutation<{ jwt: string }, DefaultError, RegisterPayload>({
        mutationFn: (payload) => register(payload).then(r => r.data),
        onSuccess: (data) => {
            authStore.setJwt(data.jwt);
            authStore.setShowRegister(false);
            navigate("/")
        },
        onError: (error) => {
            //@ts-ignore
            const er = error as AxiosError;
            if (er.code === 'ERR_NETWORK') {
                enqueueSnackbar({ message: "Please check your internet connection", variant: 'error' })
            } else {
                enqueueSnackbar({ message: "Failed to register: " + er.message, variant: 'error' })
            }
        }
    });

    useEffect(() => {
        setLoading(mutation.isPending)
    }, [setLoading, mutation.isPending]);

    const handleSubmit = (values: RegisterPayload) => {
        mutation.mutate(values);
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom textAlign={"center"}>
                Welcome to relapka.com
            </Typography>
            <Typography textAlign={"center"} className={"mb-15-i"}>
                A place where real estate is sold
            </Typography>
            <Formik
                initialValues={initialValues}
                validationSchema={LoginSchema}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, handleChange, handleBlur, values, touched, errors }) => (
                    <Form>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                name="login"
                                label="Email"
                                value={values.login}
                                onChange={handleChange}
                                onBlur={(e) => {
                                    function extractEmailName(login: string) {
                                        if(login.indexOf("@") > -1) {
                                            return login.substring(0, login.indexOf("@"));
                                        }
                                        return login;
                                    }
                                    if(values.username === undefined) {
                                        setFieldValue('username', extractEmailName(values.login), false);
                                    }
                                    handleBlur(e)
                                }}
                                error={touched.login && Boolean(errors.login)}
                                helperText={touched.login && errors.login}
                            />
                        </Box>
                        <Box mb={2}>
                            <TextInput
                                fullWidth
                                name="password"
                                label="Password"
                                type="password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && errors.password}
                            />
                        </Box>
                        <Box mb={2}>
                            <TextInput
                                fullWidth
                                name="repeat_password"
                                label="Repeat Password"
                                type="password"
                                value={repeatPassword}
                                onChange={e => setRepeatPassword(e.target.value)}
                                onBlur={handleBlur}
                                error={repeatPassword !== values.password && 'Passwords does not match'}
                            />
                        </Box>
                        <Box mb={2}>
                            <TextInput
                                fullWidth
                                name="username"
                                label="Username"
                                value={values.username}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Box>
                        <Box mb={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agreed}
                                        onChange={(e) => setAgreed(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I agree with{" "}
                                        <Link
                                            href="/terms-and-conditions"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Terms & Conditions
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Box>
                        <Stack flexDirection={"row"} justifyContent="space-between" alignItems="center">
                            <Stack>
                                <Button type="button" variant="contained" color="primary" onClick={() => {
                                    authStore.setShowLogin(true)
                                    authStore.setShowRegister(false)
                                }}>
                                    Have account?
                                </Button>
                            </Stack>
                            <Button type="submit" disabled={!agreed} variant="contained" color="primary">
                                Sign up
                            </Button>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};